import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { WalletService } from "@domain/services/wallet.service";
import type { Wallet } from "@infrastructure/clients/http/clients/wallet.client";
import { BonusIcon, describePrints, pluralBonuses } from "@boundary/components/bonusIcons/bonusIcons";
import styles from "./bonusBadge.module.scss";

/**
 * Знак наград на карте.
 *
 * Показывает одно число, а не список: на карте нет места, а человеку нужно
 * понять за полсекунды, есть ли ему чем платить. Бонусы важнее бесплатной печати —
 * их можно потратить на что угодно, а отпечаток только на свою услугу. Если
 * бонусов нет, показываем те купоны, которых больше всего.
 *
 * Пустой кошелёк тоже показываем нулём: знак должен быть на месте всегда, иначе
 * человек не узнает, что награды вообще бывают.
 */
export function BonusBadge() {
  const navigate = useNavigate();
  const [wallet, setWallet] = useState<Wallet | null>(null);

  useEffect(() => {
    let alive = true;
    WalletService.get()
      .then((next) => {
        if (alive) setWallet(next);
      })
      .catch(() => {
        // Кошелёк не обязан быть доступен, чтобы карта работала.
      });
    return () => {
      alive = false;
    };
  }, []);

  const best = wallet?.prints.slice().sort((a, b) => b.count - a.count)[0];
  const showPoints = !wallet || wallet.money > 0 || !best;

  return (
    <button
      type="button"
      className={styles.badge}
      onClick={() => navigate("/bonuses")}
      aria-label="Мои бонусы"
    >
      {showPoints ? (
        <>
          <BonusIcon kind="points" className={styles.icon} />
          <span className={styles.value}>{Math.round(wallet?.money ?? 0)}</span>
        </>
      ) : (
        <>
          <BonusIcon kind={best.service_type} className={styles.icon} />
          <span className={styles.value}>{best.count}</span>
        </>
      )}
    </button>
  );
}

/** Подпись для страницы бонусов: чем именно человек богат. */
export function bestBonusLabel(wallet: Wallet | null): string {
  if (!wallet) return "0 бонусов";
  if (wallet.money > 0) return `${Math.round(wallet.money)} ${pluralBonuses(Math.round(wallet.money))}`;

  const best = wallet.prints.slice().sort((a, b) => b.count - a.count)[0];
  return best ? describePrints(best.service_type, best.count) : "0 бонусов";
}
